import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { getWorkItemProcessRunSummary, getWorkItemProcessRunsSummary } from '~/services/searchAPI';
import {
  ApiQueryFnData,
  formatErrorPayload,
  QueryError,
  UseApiQueryOptions,
  useCurrentWorkspaceQueryKey,
  validateApiResponse,
} from '../utils';
import { useLazyQuery } from '../useLazyQuery';

const ROOT_KEY = 'workItemSummary';
export const getWorkItemSummaryByProcessRunQueryKey = (processId: string, runId: string) => [
  ROOT_KEY,
  processId,
  runId,
];
export const getWorkItemSummaryByProcessRunsNoQueryKey = (
  processId: string,
  runNumbers: number[] = [],
) => [ROOT_KEY, processId, runNumbers.join(',')];

export const useWorkItemSummaryByProcessRunQuery = <T extends typeof getWorkItemProcessRunSummary>(
  { processId, processRunId }: { processId: string; processRunId: string },
  options?: UseApiQueryOptions<T, ApiQueryFnData<T>['data']>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getWorkItemSummaryByProcessRunQueryKey(processId, processRunId),
    async () =>
      validateApiResponse(
        await getWorkItemProcessRunSummary({ workspaceId, processId, processRunId }),
      ).data?.data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export function useWorkItemSummaryByProcessRunLazyQuery<
  T extends typeof getWorkItemProcessRunSummary,
>() {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useLazyQuery<
    { processId: string; processRunId: string },
    ApiQueryFnData<T>['data'] | undefined,
    QueryError
  >(
    ({ processId, processRunId }) =>
      getWorkItemSummaryByProcessRunQueryKey(processId, processRunId),
    ({ processId, processRunId }) =>
      async () =>
        validateApiResponse(
          await getWorkItemProcessRunSummary({ workspaceId, processId, processRunId }),
        ).data?.data,
    {
      onError: (error: Error) => dispatch(formatErrorPayload(error)),
      staleTime: 2000,
      refetchInterval: 10000,
    },
  );
}

export function useWorkItemSummaryByProcessRunsNoQuery<
  T extends typeof getWorkItemProcessRunsSummary,
>(
  { processId, runs = [] }: { processId: string; runs: number[] },
  options?: UseApiQueryOptions<T, ApiQueryFnData<T>['data']>,
) {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getWorkItemSummaryByProcessRunsNoQueryKey(processId, runs),
    async () =>
      validateApiResponse(
        await getWorkItemProcessRunsSummary({ workspaceId, processId, runNo: runs }),
      ).data?.data,
    {
      onError: (error: Error) => dispatch(formatErrorPayload(error)),
      staleTime: 2000,
      enabled: runs.length > 0,
      refetchInterval: 10000,
      ...options,
    },
  );
}
